import type { ThreadBindings, ThreadSettings } from "../../../api/threads/liveMessages";
import { changeSettings, renameThread } from "../../../api/threads/threadsApi";
import { toUiError, UiErrorException } from "../../../errors/uiError";

/**
 * Change a Thread's settings (host only). The change is conditional on the last entry the
 * host saw (T-1); when the log moved on meanwhile, it is sent once more on the number the
 * server reports, because a settings change does not depend on what was said in between.
 */
export async function changeThreadSettings(
  threadId: string,
  expectedLastEntryNumber: number,
  settings: ThreadSettings,
  bindings: ThreadBindings,
): Promise<void> {
  await onTheNewestEntry(expectedLastEntryNumber, (expected) => changeSettings(threadId, expected, settings, bindings));
}

/** Rename the Thread (host only, F68); like a settings change, it doesn't depend on what was said meanwhile. */
export async function changeThreadTitle(threadId: string, expectedLastEntryNumber: number, title: string): Promise<void> {
  await onTheNewestEntry(expectedLastEntryNumber, (expected) => renameThread(threadId, expected, title));
}

/** `send` on the number the host saw, and once more on the server's newest if the log moved on. */
async function onTheNewestEntry(expectedLastEntryNumber: number, send: (expected: number) => Promise<unknown>): Promise<void> {
  try {
    await send(expectedLastEntryNumber);
  } catch (caught) {
    const error = toUiError(caught);
    const reported = error.kind === "problem" && error.problem.code === "thread_log_conflict" ? error.problem.details["last_entry_number"] : undefined;
    if (typeof reported !== "number") {
      throw caught instanceof UiErrorException ? caught : new UiErrorException(error);
    }
    await send(reported);
  }
}
